// src/components/tags/TagPickerTrello.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, TextInput } from "flowbite-react";
import { HiOutlinePencil, HiTrash, HiPlus } from "react-icons/hi";
import { TAGS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";

const PALETTE = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#10b981",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#6b7280",
  "#111827",
];

export default function TagPicker({
  companyId,
  funnelId,
  cardId,
  currentTag, // { id,name,color } ou null
  onClose,
  onAssigned, // callback(tag || null) quando atribui/remover
}) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // objeto tag sendo editado
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState("#10b981");
  const [creating, setCreating] = useState(false);
  const canManageTags = Boolean(companyId && funnelId);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(term));
  }, [q, tags]);

  const load = async () => {
    if (!canManageTags) {
      setTags([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(TAGS.LIST(companyId), {
        params: { limit: 200, funnelId },
      });
      const items =
        res?.data?.data?.items || res?.data?.data || res?.data || [];
      setTags(items);
    } catch (e) {
      console.error("Tags LIST error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId, funnelId]);

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setFormName("");
    setFormColor("#10b981");
  };
  const startEdit = (t) => {
    setEditing(t);
    setCreating(false);
    setFormName(t.name || "");
    setFormColor(t.color || "#10b981");
  };
  const resetForm = () => {
    setEditing(null);
    setCreating(false);
    setFormName("");
    setFormColor("#10b981");
  };

  const createTag = async () => {
    if (!formName.trim()) return;
    if (!canManageTags) {
      alert("Informe um funil antes de criar etiquetas.");
      return;
    }
    try {
      await axios.post(TAGS.CREATE(companyId), {
        name: formName.trim(),
        color: formColor,
        funnelId,
      });
      await load();
      resetForm();
    } catch (e) {
      console.error("Tag CREATE error", e);
      alert(e?.response?.data?.error || "Não foi possível criar a tag.");
    }
  };
  const updateTag = async () => {
    if (!editing) return;
    if (!canManageTags) {
      alert("Informe um funil antes de atualizar etiquetas.");
      return;
    }
    try {
      await axios.put(TAGS.UPDATE(companyId, editing.id), {
        name: formName.trim(),
        color: formColor,
        funnelId: editing.funnelId || funnelId,
      });
      await load();
      resetForm();
    } catch (e) {
      console.error("Tag UPDATE error", e);
      alert(e?.response?.data?.error || "Não foi possível atualizar a tag.");
    }
  };
  const deleteTag = async (t) => {
    const ok = window.confirm(`Excluir a tag "${t.name}"?`);
    if (!ok) return;
    try {
      await axios.delete(TAGS.DELETE(companyId, t.id));
      await load();
      // se a tag deletada era a atual do card, desatribuir no pai
      if (currentTag?.id === t.id) onAssigned?.(null);
    } catch (e) {
      console.error("Tag DELETE error", e);
      alert(e?.response?.data?.error || "Não foi possível excluir a tag.");
    }
  };

  const assignToCard = async (t) => {
    try {
      // quem salva no card é o pai via PUT /crm/card/:id com { tagId }
      onAssigned?.(t);
    } catch (e) {
      console.error("Assign error", e);
    }
  };

  return (
    <Modal
      show={open}
      size="xl"
      onClose={() => {
        setOpen(false);
        onClose?.();
      }}
    >
      <Modal.Header>Etiquetas - Tags</Modal.Header>
      <Modal.Body>
        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            {!creating && !editing && (
              <TextInput
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar etiqueta..."
                className="flex-1"
              />
            )}

            {!creating && !editing && (
              <Button
                size="sm"
                className="flex items-center"
                onClick={startCreate}
              >
                Nova etiqueta
              </Button>
            )}
          </div>

          {/* Form de criar/editar */}
          {(creating || editing) && (
            <div className="rounded-md space-y-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">
                  Nome da etiqueta
                </label>
                <TextInput
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex.: Prioritário"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 block mb-2">Cor</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="color"
                    value={formColor}
                    onChange={(e) => setFormColor(e.target.value)}
                    className="h-9 w-9 rounded border cursor-pointer"
                  />
                  {PALETTE.map((c) => (
                    <button
                      type="button"
                      key={c}
                      className="h-7 w-7 rounded border"
                      style={{ background: c }}
                      onClick={() => setFormColor(c)}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button size="sm" color="light" onClick={resetForm}>
                  Cancelar
                </Button>
                {editing ? (
                  <Button size="sm" onClick={updateTag}>
                    Salvar mudanças
                  </Button>
                ) : (
                  <Button size="sm" onClick={createTag}>
                    Criar etiqueta
                  </Button>
                )}
              </div>
            </div>
          )}

          {!creating && !editing && (
            <>
              {!canManageTags ? (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-700">
                  Informe o funil (etapa) do card para listar as etiquetas deste
                  funil.
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {loading ? (
                    <div className="text-sm text-gray-500">Carregando...</div>
                  ) : filtered.length ? (
                    filtered.map((t) => {
                      const isCurrent = currentTag?.id === t.id;
                      return (
                        <div
                          key={t.id}
                          className="flex items-center justify-between gap-2"
                        >
                          <button
                            className={`flex-1 flex items-center gap-3 rounded-md border px-2 py-2 text-left hover:bg-gray-50 transition ${
                              isCurrent ? "border-gray-400" : "border-gray-200"
                            }`}
                            onClick={() => assignToCard(t)}
                            title="Atribuir esta etiqueta ao card"
                          >
                            <span
                              className="inline-block h-5 w-8 rounded"
                              style={{ background: t.color || "#6b7280" }}
                            />
                            <span className="text-sm font-medium text-gray-800">
                              {t.name}
                            </span>
                            {isCurrent && (
                              <span className="ml-auto text-xs text-gray-500">
                                (atual)
                              </span>
                            )}
                          </button>
                          <button
                            className="px-2 py-2 rounded border border-gray-200 hover:bg-gray-50"
                            title="Editar"
                            onClick={() => startEdit(t)}
                          >
                            <HiOutlinePencil size={16} />
                          </button>
                          <button
                            className="px-2 py-2 rounded border border-gray-200 hover:bg-red-50 text-red-600"
                            title="Excluir"
                            onClick={() => deleteTag(t)}
                          >
                            <HiTrash size={16} />
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-500">Nenhuma etiqueta.</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
